import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import '../Components/AuditOperationDetail.css';
import Breadcrumbs from '../Components/Breadcrumbs';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import {
  AuditOperationsApiProvider,
  useAuditOperationsApi,
} from '../Services/AuditOperationsApiProvider';
import { formatDateUTC } from '../utils/formatDate';

function AuditOperationDetail() {
  const { auditOperationId } = useParams();
  const { getAuditOperationById } = useAuditOperationsApi();

  const [loading, setLoading] = useState(false);
  const [auditOperation, setAuditOperation] = useState(null);

  const isNotificationTrigger =
    auditOperation?.operation_type === 'notification:notification.trigger';
  const isConnectionDispatch = auditOperation?.operation_type?.includes('connection_dispatch');
  const isServiceBusDeadMessage = auditOperation?.operation_type?.includes(
    'service_bus_dead_message',
  );
  const isTaskSchedule = auditOperation?.operation_type?.includes('task_schedule');

  const changes = auditOperation?.properties?.changes;
  const entityProperties = auditOperation?.properties?.entity_properties;

  const connectionDispatchProperties = isConnectionDispatch
    ? auditOperation?.properties || {}
    : null;
  const connectionObjectAttributes =
    connectionDispatchProperties?.connection_object_attribute?.object_attributes;

  // 👇 хелпер щоб мати object навіть якщо бек віддав строку
  const parseJsonMaybe = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return null;

    try {
      return JSON.parse(value);
    } catch (e) {
      // іноді може бути str(dict) з одинарними лапками
      try {
        return JSON.parse(value.replace(/'/g, '"'));
      } catch (e2) {
        return null;
      }
    }
  };
  const isEmptyValue = (val) =>
    val === null ||
    val === undefined ||
    (typeof val === 'string' && val.trim() === '') ||
    (Array.isArray(val) && val.length === 0) ||
    (typeof val === 'object' && true && Object.keys(val).length === 0);

  const extractValue = (val, indent = 0) => {
    if (isEmptyValue(val)) return '—';
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
      return String(val);
    }

    if (Array.isArray(val)) {
      return (
        val
          .map((v) => extractValue(v, indent))
          .filter((v) => v !== '—')
          .join('\n') || '—'
      );
    }

    if (typeof val === 'object') {
      const pad = '  '.repeat(indent);

      const lines = Object.entries(val)
        .map(([key, value]) => {
          if (isEmptyValue(value)) return null;

          if (typeof value === 'object' && !Array.isArray(value)) {
            const nested = extractValue(value, indent + 1);
            return nested === '—' ? null : `${pad}${key}:\n${nested}`;
          }

          return `${pad}${key}: ${extractValue(value, indent)}`;
        })
        .filter(Boolean);

      return lines.length ? lines.join('\n') : '—';
    }

    return String(val);
  };

  const rawServiceBusMetadata = isServiceBusDeadMessage
    ? auditOperation?.properties?.metadata
    : null;
  const serviceBusMetadata = isServiceBusDeadMessage ? auditOperation.properties : null;
  const serviceBusEventPayload = serviceBusMetadata?.event_payload;

  const taskScheduleProperties = isTaskSchedule ? auditOperation?.properties || {} : null;

  const sentPayload = isConnectionDispatch
    ? parseJsonMaybe(auditOperation?.properties?.sent_payload)
    : null;

  const formatFieldName = (field) => {
    return field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  };

  const recordRouteObjectMapper = isConnectionDispatch
    ? parseJsonMaybe(connectionDispatchProperties?.record_route_object_mapper)
    : null;

  const recordRouteToMapping = isConnectionDispatch
    ? parseJsonMaybe(connectionDispatchProperties?.record_route_to_connection_object_mapping)
    : null;

  const recordRouteFromMapping = isConnectionDispatch
    ? parseJsonMaybe(connectionDispatchProperties?.record_route_from_connection_object_mapping)
    : null;

  const connectionDispatchResult = isConnectionDispatch
    ? parseJsonMaybe(connectionDispatchProperties?.result)
    : null;

  useEffect(() => {
    const fetchAuditOperation = async () => {
      setLoading(true);
      try {
        console.log('Fetching audit operation with ID:', auditOperationId);
        const response = await getAuditOperationById(auditOperationId);
        if (response) {
          setAuditOperation(response);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAuditOperation();
  }, [auditOperationId]);

  return (
    <div className="container">
      <Breadcrumbs />

      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          <h2 className="h5-title">Audit operations</h2>
          <div className="radius-yes bg-yes">
            {isNotificationTrigger ? (
              <>
                <div className="detail-item">
                  <label>
                    <strong>Operation Id:</strong>
                  </label>
                  <span>{auditOperation?.id}</span>
                </div>
                <div className="detail-item">
                  <label>
                    <strong>Created At:</strong>
                  </label>
                  <span>
                    {auditOperation?.created_at ? formatDateUTC(auditOperation.created_at) : '-'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>
                    <strong>Operation Type:</strong>
                  </label>
                  <span>{auditOperation?.operation_type}</span>
                </div>
                <div className="detail-item">
                  <label>
                    <strong>Notification Id:</strong>
                  </label>
                  <span>{auditOperation?.properties?.notification_key || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>
                    <strong>Notification Text:</strong>
                  </label>
                  <span>{auditOperation?.properties?.notification_text || '-'}</span>
                </div>
              </>
            ) : (
              <>
                <div className="detail-item">
                  <label>
                    <strong>Operation Id:</strong>
                  </label>
                  <span>{auditOperation?.key}</span>
                </div>
                <div className="detail-item">
                  <label>
                    <strong>Status:</strong>
                  </label>
                  <span
                    className={`status-badge ${auditOperation?.status === 'success' ? 'success' : 'failure'}`}
                  >
                    {auditOperation?.status || '-'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>
                    <strong>Created At:</strong>
                  </label>
                  <span>
                    {auditOperation?.created_at ? formatDateUTC(auditOperation.created_at) : '-'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>
                    <strong>Operation Type:</strong>
                  </label>
                  <span>{auditOperation?.operation_type}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {!isNotificationTrigger && (
          <div className="radius-yes p-2 bg-yes col-12">
            <h2 className="h5-title">Operation Details</h2>
            <div className="radius-yes bg-yes">
              {isServiceBusDeadMessage ? (
                <>
                  <div className="detail-item">
                    <label>
                      <strong>Event topic:</strong>
                    </label>
                    <span>{serviceBusMetadata?.event_topic || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Event subject:</strong>
                    </label>
                    <span>{serviceBusMetadata?.event_subject || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Delivery count:</strong>
                    </label>
                    <span>{serviceBusMetadata?.delivery_count ?? '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Exception type:</strong>
                    </label>
                    <span>{serviceBusMetadata?.exception_type || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Exception message:</strong>
                    </label>
                    <span>{serviceBusMetadata?.exception_message || '-'}</span>
                  </div>

                  {/* якщо не змогли розпарсити – показати raw */}
                  {!serviceBusMetadata && rawServiceBusMetadata && (
                    <div className="radius-yes bg-yes mt-2 p-2">
                      <h3 className="h5-title mb-2">Raw metadata</h3>
                      <div className="detail-item">
                        <label>
                          <strong>Value:</strong>
                        </label>
                        <span>{formatValue(rawServiceBusMetadata)}</span>
                      </div>
                    </div>
                  )}

                  {serviceBusEventPayload && typeof serviceBusEventPayload === 'object' && (
                    <div className="radius-yes bg-yes mt-2 p-2">
                      <h3 className="h5-title mb-2">Event payload</h3>
                      {Object.entries(serviceBusEventPayload).map(([key, value]) => (
                        <div className="detail-item" key={key}>
                          <label>
                            <strong>{formatFieldName(key)}:</strong>
                          </label>
                          <span>{formatValue(value)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : isConnectionDispatch ? (
                <>
                  {Object.entries(connectionDispatchProperties)
                    .filter(
                      ([key]) =>
                        key !== 'result' &&
                        key !== 'status' &&
                        key !== 'connection_object_attribute',
                    )
                    .map(([key, value]) => (
                      <div className="detail-item" key={key}>
                        <label>
                          <strong>{formatFieldName(key)}:</strong>
                        </label>
                        <span>{formatValue(value)}</span>
                      </div>
                    ))}
                </>
              ) : isTaskSchedule ? (
                <>
                  <div className="detail-item">
                    <label>
                      <strong>Task Schedule Key:</strong>
                    </label>
                    <span>{taskScheduleProperties?.task_schedule_key || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Assistant Key:</strong>
                    </label>
                    <span>{taskScheduleProperties?.assistant_key || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Execution Key:</strong>
                    </label>
                    <span>{taskScheduleProperties?.execution_key || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Slot Key:</strong>
                    </label>
                    <span>{taskScheduleProperties?.slot_key || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Exception Type:</strong>
                    </label>
                    <span>{taskScheduleProperties?.exception_type || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Exception Message:</strong>
                    </label>
                    <span>{taskScheduleProperties?.exception_message || '-'}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="detail-item">
                    <label>
                      <strong>User Id:</strong>
                    </label>
                    <span>{auditOperation?.user_key || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Operation Request Id:</strong>
                    </label>
                    <span>{auditOperation?.properties?.request_id || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Record Id:</strong>
                    </label>
                    <span>{auditOperation?.properties?.entity_id || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Remote IP:</strong>
                    </label>
                    <span>{auditOperation?.properties?.client_ip || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>User Agent:</strong>
                    </label>
                    <span>{auditOperation?.properties?.user_agent || '-'}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {isConnectionDispatch &&
          Array.isArray(connectionObjectAttributes) &&
          connectionObjectAttributes.length > 0 && (
            <div className="radius-yes p-2 bg-yes col-12">
              <h2 className="h5-title">Object attributes</h2>
              <div className="radius-yes bg-yes">
                <div className="w-100">
                  {connectionObjectAttributes.map((attr, index) => {
                    const sentValue = sentPayload ? sentPayload[attr.attribute_name] : undefined;

                    return (
                      <div className="radius-yes bg-yes mb-2 p-2" key={index}>
                        <div className="detail-item">
                          <label>
                            <strong>Name:</strong>
                          </label>
                          <span>{attr.attribute_name}</span>
                        </div>

                        <div className="detail-item">
                          <label>
                            <strong>Sent Value:</strong>
                          </label>
                          <span>{formatValue(sentValue)}</span>
                        </div>

                        <div className="detail-item">
                          <label>
                            <strong>Type:</strong>
                          </label>
                          <span>{attr.attribute_type}</span>
                        </div>
                        <div className="detail-item">
                          <label>
                            <strong>Required:</strong>
                          </label>
                          <span>{attr.is_required ? 'Yes' : 'No'}</span>
                        </div>
                        <div className="detail-item">
                          <label>
                            <strong>Default Value:</strong>
                          </label>
                          <span>{attr.default_value || '-'}</span>
                        </div>
                        <div className="detail-item">
                          <label>
                            <strong>Parameter Location:</strong>
                          </label>
                          <span>{attr.parameter_location || '-'}</span>
                        </div>
                        <div className="detail-item">
                          <label>
                            <strong>Description:</strong>
                          </label>
                          <span>{attr.description || '-'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

        {!isNotificationTrigger && (changes || entityProperties) && (
          <div className="radius-yes p-2 bg-yes col-12">
            <h2 className="h5-title">{changes ? 'Changes' : 'Create Properties'}</h2>
            <div className="radius-yes bg-yes">
              {Object.entries(changes || entityProperties).map(([field, value]) => (
                <div className="detail-item" key={field}>
                  <label>
                    <strong>{formatFieldName(field)}:</strong>
                  </label>
                  <span style={{ whiteSpace: 'pre-line' }}>
                    {changes
                      ? `${extractValue(value.old)} → ${extractValue(value.new)}`
                      : extractValue(value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AuditOperationsWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <AuditOperationsApiProvider>
        <AuditOperationDetail />
      </AuditOperationsApiProvider>
    </ConfirmationProvider>
  );
}

export default AuditOperationsWrapper;
